import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Search, Layers } from "lucide-react";
import QRModal from "./QRModal";
import LogsModal from "./LogsModal";
import TraceabilityModal from "./TraceabilityModal";
import OwnerModal from "./OwnerModal";
import LotDropModal from "./LotDropModal";
import ExportOwnersButton from "./ExportOwnersButton";
import CreateCodeModal from "./CreateCodeModal";
import CodeRow from "./CodeRow";
import ProductSheetModal from "./ProductSheetModal";
import { toLocalInput, fromLocalInput } from "./codes-utils";

const EMPTY_EDIT = { label: "", fileId: "", expiresAt: "", maxUses: "", notifyEmail: "" };

export default function AdminCodes() {
  const [codes, setCodes] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [showLotDrop, setShowLotDrop] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState(EMPTY_EDIT);

  const [qrCode, setQrCode] = useState(null);
  const [logsCode, setLogsCode] = useState(null);
  const [traceCode, setTraceCode] = useState(null);
  const [ownerCode, setOwnerCode] = useState(null);
  const [sheetCode, setSheetCode] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, f] = await Promise.all([api.get("/admin/codes"), api.get("/admin/files")]);
      setCodes(c.data);
      setFiles(f.data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const copy = useCallback((val) => {
    navigator.clipboard.writeText(val);
    toast.success("Code copied");
  }, []);

  const startEdit = useCallback((c) => {
    setEditingId(c.id);
    setEditValues({
      label: c.label || "",
      fileId: c.file_id || "",
      expiresAt: toLocalInput(c.expires_at),
      maxUses: c.max_uses ? String(c.max_uses) : "",
      notifyEmail: c.notify_email || "",
    });
  }, []);

  const cancelEdit = useCallback(() => setEditingId(null), []);

  const onEditValueChange = useCallback((key, value) => {
    setEditValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const saveEdit = useCallback(async (id) => {
    try {
      await api.patch(`/admin/codes/${id}`, {
        label: editValues.label,
        file_id: editValues.fileId || "",
        expires_at: editValues.expiresAt ? fromLocalInput(editValues.expiresAt) : "",
        max_uses: editValues.maxUses ? parseInt(editValues.maxUses, 10) : 0,
        notify_email: editValues.notifyEmail.trim(),
      });
      toast.success("Updated");
      setEditingId(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  }, [editValues, fetchAll]);

  const toggleActive = useCallback(async (c) => {
    try {
      await api.patch(`/admin/codes/${c.id}`, { is_active: !c.is_active });
      fetchAll();
    } catch {
      toast.error("Failed");
    }
  }, [fetchAll]);

  const remove = useCallback(async (id) => {
    if (!window.confirm("Delete this code? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/codes/${id}`);
      toast.success("Deleted");
      fetchAll();
    } catch {
      toast.error("Failed");
    }
  }, [fetchAll]);

  const filtered = codes.filter((c) => {
    const q = search.toLowerCase();
    return !q || c.code.toLowerCase().includes(q) || (c.label || "").toLowerCase().includes(q) || (c.file_name || "").toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-codes" className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline text-[var(--vault-text-muted)] mb-2">Cellar</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Access Codes</h1>
          <p className="text-[var(--vault-text-muted)] mt-3">Issue, edit and revoke the codes that unlock each bottle's documents.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
        <button
          data-testid="new-code-button"
          onClick={() => setShowCreate(true)}
          className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm"
        >
          <Plus className="w-4 h-4" />
          <span>New Code</span>
        </button>
        <button
          data-testid="lot-drop-button"
          onClick={() => setShowLotDrop(true)}
          className="btn-ghost inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm"
        >
          <Layers className="w-4 h-4" />
          <span>Lot Drop</span>
        </button>
        <ExportOwnersButton />
        </div>
      </header>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vault-text-muted)]" />
        <input
          data-testid="codes-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search code, label or file…"
          className="w-full pl-10 pr-4 py-2.5 rounded-md text-sm outline-none"
          style={{ background: "var(--vault-surface)", border: "1px solid var(--vault-border)" }}
        />
      </div>

      <div
        className="rounded-lg overflow-hidden border"
        style={{ borderColor: "var(--vault-border)", background: "var(--vault-surface)" }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left overline text-[var(--vault-text-muted)]">
                <th className="px-4 py-4">Code</th>
                <th className="px-4 py-4">Label</th>
                <th className="px-4 py-4">Linked File</th>
                <th className="px-4 py-4">Uses</th>
                <th className="px-4 py-4">Expires</th>
                <th className="px-4 py-4">Last Access</th>
                <th className="px-4 py-4">Status</th>
                <th className="px-4 py-4 text-right sticky right-0 z-10" style={{ background: "var(--vault-surface)" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[var(--vault-text-muted)]">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-[var(--vault-text-muted)]">
                  No codes yet. Create your first access code.
                </td></tr>
              )}
              {!loading && filtered.map((c) => (
                <CodeRow
                  key={c.id}
                  code={c}
                  files={files}
                  editing={editingId === c.id}
                  editValues={editValues}
                  onEditValueChange={onEditValueChange}
                  onCopy={copy}
                  onStartEdit={startEdit}
                  onSaveEdit={saveEdit}
                  onCancelEdit={cancelEdit}
                  onToggleActive={toggleActive}
                  onDelete={remove}
                  onQR={setQrCode}
                  onLogs={setLogsCode}
                  onTrace={setTraceCode}
                  onOwner={setOwnerCode}
                  onSheet={setSheetCode}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateCodeModal
          files={files}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchAll(); }}
        />
      )}

      {qrCode && <QRModal code={qrCode} onClose={() => setQrCode(null)} />}
      {logsCode && <LogsModal code={logsCode} onClose={() => setLogsCode(null)} />}
      {traceCode && <TraceabilityModal code={traceCode} onClose={() => setTraceCode(null)} />}
      {ownerCode && <OwnerModal code={ownerCode} onClose={() => setOwnerCode(null)} onSaved={() => { setOwnerCode(null); fetchAll(); }} />}
      {sheetCode && (
        <ProductSheetModal
          codeRecord={sheetCode}
          onClose={() => setSheetCode(null)}
          onSaved={() => { setSheetCode(null); fetchAll(); }}
        />
      )}
      {showLotDrop && (
        <LotDropModal
          files={files}
          onClose={() => setShowLotDrop(false)}
          onDone={() => { setShowLotDrop(false); fetchAll(); }}
        />
      )}
    </div>
  );
}
